// FILE: src/components/FoundersWallPage.js
//
// Public Founder Wall — "Community → Founding Testers". A commemorative granite
// monument honoring the original beta contributors who helped shape PM Pixel
// before public launch. Reachable at ?view=founding-testers, no auth required.
//
// Tone: a hall of recognition — engraved names on stone, never a leaderboard.
// Data comes from the public founder_wall() RPC (safe display fields only); the
// wall renders single-line display names, ordered by award date.
//
// No Redux. Functional component, hooks only.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './FoundersWallPage.module.css';
import { getFounderWall } from '../../services/foundingTesterService';

const BADGE_ART = '/themes/pm-pixel/achievements/badge-founding-tester.png';
const MASCOT_ART = '/themes/pm-pixel/founders/mascot-cheer.png';

const MILESTONES = [
  { key: 'beta', label: 'Beta Opens', when: 'Q1 2026' },
  { key: 'first', label: 'First Testers', when: 'Q2 2026' },
  { key: 'pmbok', label: 'PMBOK® 8 Launch', when: 'Q3 2026' },
  { key: 'launch', label: 'Public Launch', when: 'Q4 2026' },
];

// One uniform "monument" size across every column; shrink all names together
// until none overflow their column. Applied imperatively to avoid re-render loops.
function fitNames(container) {
  if (!container) return;
  const cells = Array.from(container.querySelectorAll('[data-name]'));
  if (!cells.length) return;
  let fs = 24;
  const apply = () => cells.forEach((c) => { c.style.fontSize = `${fs}px`; });
  apply();
  const overflows = () => cells.some((c) => c.scrollWidth > c.clientWidth + 0.5);
  while (overflows() && fs > 12) { fs -= 0.5; apply(); }
}

function navigateHome() {
  try {
    window.dispatchEvent(new CustomEvent('pm-pixel:navigate', { detail: { view: 'home' } }));
  } catch (_) { /* non-fatal */ }
}

function MilestoneIcon({ kind }) {
  const common = {
    fill: 'none', stroke: 'currentColor', strokeWidth: 1.8,
    strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  const path = {
    beta: 'M9 3h6M10 3v6l-5 8.5A2 2 0 0 0 6.8 21h10.4a2 2 0 0 0 1.8-3.5L14 9V3M8 14h8',
    first: 'M8.5 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7 0a2.6 2.6 0 1 0 0-5.2M3.5 19v-1.2A3.8 3.8 0 0 1 7.3 14h2.4a3.8 3.8 0 0 1 3.8 3.8V19M15 14h1.2a3.8 3.8 0 0 1 3.8 3.8V19',
    pmbok: 'M12 6.5C10.5 5 8 4.5 4.5 5v12c3.5-.5 6 0 7.5 1.5M12 6.5C13.5 5 16 4.5 19.5 5v12c-3.5-.5-6 0-7.5 1.5M12 6.5V18',
    launch: 'M5 15c-1.5 1.2-2 4-2 4s2.8-.5 4-2M14.5 4.5C17 4 20 5 20 5s1 3 .5 5.5c-.6 3-3 5.5-6.5 7L9 14l-1-1c1.5-3.5 4-5.9 7-6.5ZM15 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  }[kind];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={path} {...common} />
    </svg>
  );
}

export default function FoundersWallPage() {
  const [testers, setTesters] = useState(null); // null = loading
  const [cols, setCols] = useState(4);
  const namesRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getFounderWall().then((rows) => { if (alive) setTesters(rows); });
    return () => { alive = false; };
  }, []);

  // Derive column count from the wall's REAL width (ResizeObserver), not the
  // viewport — so the pedestal narrowing the wall can never crowd the names
  // into overlap. ~205px per column fits the longest name with headroom.
  useLayoutEffect(() => {
    const el = namesRef.current;
    if (!el) return undefined;
    const recompute = () => {
      const w = el.clientWidth || window.innerWidth;
      const c = Math.max(1, Math.min(4, Math.floor(w / 205)));
      setCols((prev) => (prev === c ? prev : c));
    };
    recompute();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(recompute); ro.observe(el); }
    else { window.addEventListener('resize', recompute); }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', recompute); };
  }, [testers]);

  // Re-fit names after data/columns change, after web fonts load, and on resize.
  useLayoutEffect(() => {
    const el = namesRef.current;
    if (!el) return undefined;
    const run = () => fitNames(el);
    run();
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(() => {});
    }
    window.addEventListener('resize', run);
    return () => window.removeEventListener('resize', run);
  }, [testers, cols]);

  const loading = testers === null;
  const names = loading ? [] : testers.map((t) => t.display_name).filter(Boolean);
  const isEmpty = !loading && names.length === 0;

  const per = Math.ceil(names.length / cols) || 1;
  const columns = Array.from({ length: cols }, (_, c) => names.slice(c * per, (c + 1) * per));

  const pub = process.env.PUBLIC_URL || '';
  const pageVars = {
    '--stone-tex': `url(${pub}/themes/pm-pixel/founders/stone-bg.png)`,
    '--granite-tex': `url(${pub}/themes/pm-pixel/founders/granite.png)`,
  };

  return (
    <div className={styles.page} style={pageVars} data-testid="founders-wall-page">
      <div className={styles.wrap}>
        <button type="button" className={styles.backLink} onClick={navigateHome} data-testid="founders-wall-back">
          {'‹'} Back to PM Pixel
        </button>

        {/* ── hero ── */}
        <header className={styles.hero}>
          <div className={styles.heroTop}>
            <div className={styles.heroText}>
              <h1 className={styles.heroTitle}>Founding Testers — Class of 2026</h1>
              <p className={styles.lede}>
                The original community of learners who tested early, gave feedback, and
                helped shape PM&nbsp;Pixel into the best PMP&reg; exam-prep experience.
                The program is complete and enrollment is closed — these names are
                permanent.
              </p>
              <p className={styles.thanks}>Thank you for helping build PM&nbsp;Pixel.</p>
            </div>
            <img className={styles.heroBadge} src={BADGE_ART} alt="Founding Tester badge" />
          </div>
        </header>

        {/* ── stage: statue + wall ── */}
        <main className={styles.stage}>
          <div className={styles.pedestalCol}>
            <img className={styles.mascot} src={MASCOT_ART} alt="PM Pixel mascot cheering on a pedestal" />
            <div className={styles.plinth}>
              <div className={styles.cornice} aria-hidden="true">
                <div className={styles.corniceCap} />
                <div className={styles.corniceThroat} />
              </div>
              <div className={styles.die}>
                <div className={styles.plaque}>
                  <div className={styles.plaqueInner}>
                    <span className={styles.tagText}>Prepare. Practice. Pass.</span>
                  </div>
                </div>
              </div>
              <div className={styles.base} aria-hidden="true">
                <span className={`${styles.tier} ${styles.t1}`} />
                <span className={`${styles.tier} ${styles.t2}`} />
                <span className={`${styles.tier} ${styles.t3}`} />
              </div>
            </div>
          </div>

          <section className={styles.wall} aria-label="Founding Testers recognition wall">
            {loading && <p className={styles.loading} data-testid="founders-wall-loading">Loading the wall{'…'}</p>}
            {isEmpty && (
              <div className={styles.empty} data-testid="founders-wall-empty">
                <h2 className={styles.emptyTitle}>The wall is being prepared</h2>
                <p>Founding Testers are recognized by hand. Their names will be inscribed here soon.</p>
              </div>
            )}
            {!loading && !isEmpty && (
              <div className={styles.names} ref={namesRef} data-testid="founders-wall-names">
                {columns.map((colNames, ci) => (
                  <ul className={styles.namesCol} key={`col-${ci}`}>
                    {colNames.map((n, ni) => (
                      <li className={styles.name} data-name key={`${n}-${ni}`}>{n}</li>
                    ))}
                  </ul>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* ── timeline ── */}
        <section className={styles.timeline} aria-label="PM Pixel history">
          <div className={styles.tlTrack}>
            {MILESTONES.map((m) => (
              <div className={styles.tlNode} key={m.key}>
                <span className={styles.tlDot}><MilestoneIcon kind={m.key} /></span>
                <span className={styles.tlLabel}>{m.label}</span>
                <span className={styles.tlWhen}>{m.when}</span>
              </div>
            ))}
          </div>
          <div className={styles.tlEnd}>
            <span className={styles.tlCap} aria-hidden="true" />
            <span className={styles.tlEst}><small>Est.</small><b>2026</b></span>
          </div>
        </section>

        {/* ── closing ── */}
        <div className={styles.closing}>
          <span className={styles.closingStar} aria-hidden="true">{'★'}</span>
          <p>Every learner who passes because of PM&nbsp;Pixel benefits from the contributions of these original testers.</p>
          <span className={styles.closingStar} aria-hidden="true">{'★'}</span>
        </div>
        <div className={styles.footzone} />
      </div>
    </div>
  );
}
